import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import Typography from "@mui/material/Typography";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import GetAppIcon from "@mui/icons-material/GetApp";

import { OkIcon, ExpiredIcon } from "../../components/CourseStatusIcon";
import { StyledSwitch } from "../../components/Base";
import Button from "../../components/Button";
import TableComponent from "../../components/TableComponent";
import { openExportWindow } from "../../api/index";
import useCrew from "../../hooks/useCrew";
import useUser from "../../hooks/useUser";
import {
  getUsersInfoTraining,
  updateCheckingProgress,
  updateModuleStatus,
  updateStatusCertificate,
} from "../../api/training.deprecated";
import { isClassroomModuleCompleted, isPracticalModuleCompleted, isTrainingCompleted } from "../../api/training";
import { SignOffDateButton, useCanEditSignOffDate } from "../../features/training-sign-off-date";

/**
 * One sign-off row inside a module cell: its label, the toggle while the module is still
 * open, and the pencil once the flag is granted. The switches are hidden after the module
 * completes, so without the pencil here a finished sign-off would have no editable date.
 */
const SignOffControl = ({ label, title, field, userInfo, showSwitch, onToggle, onSaved }) => {
  const canEditDate = useCanEditSignOffDate(field);
  const value = userInfo[field];
  const signedOff = value === 1;
  const showPencil = signedOff && canEditDate;

  // With the module complete there is no switch, so without an editable date there is
  // nothing left to show — render nothing rather than a label on its own.
  if (!showSwitch && !showPencil) return null;

  return (
    <p>
      {label}:
      {showSwitch && (
        <StyledSwitch
          title={title}
          checked={signedOff}
          onChange={() => onToggle(userInfo.id, field, signedOff ? 0 : 1, userInfo)}
        />
      )}
      {showPencil && (
        <SignOffDateButton
          trainingUserId={userInfo.id}
          field={field}
          date={userInfo[`${field}_date`]}
          onSaved={onSaved}
        />
      )}
    </p>
  );
};

const UsersTrainingInscribedTable = ({ match, history }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const onExportError = () => enqueueSnackbar(t("orders.document_not_found"), { variant: "error" });
  const [usersInfo, setUsersInfo] = useState(null);
  const [refresh, setRefresh] = useState(1);
  const [eLearning, setELearning] = useState(0);
  const [classroom, setClassroom] = useState(0);
  const [practical, setPractical] = useState(0);
  const [classroomAttendance, setClassroomAttendance] = useState(0);
  const [classroomCheck, setClassroomCheck] = useState(0);

  const [instructorComplete, setInstructorComplete] = useState(0);
  const [assessorPassed, setAssessorPassed] = useState(0);
  const [checkerPassed, setCheckerPassed] = useState(0);

  const [courseId, setCourseId] = useState(null);

  const { crew } = useCrew();
  const evaluator = useUser();
  useEffect(() => {
    (async () => {
      try {
        const trainingId = match.params.trainingId;
        const trainingRole = match.params.trainingRole;

        const res = await getUsersInfoTraining(trainingId, crew.id, trainingRole, crew.baseIds);

        if (!res?.User2Trainings) {
          setUsersInfo([]);
          return;
        }

        // Filter users based on crew ID and training role
        let filteredUsers = res.User2Trainings;

        // Only filter by crew if crew.id > 0
        if (crew.id > 0) {
          // First, check if the user has a role in this crew
          const userHasRoleInCrew = filteredUsers.some(
            (item) => item.Crew?.id === crew.id && item.training_role === trainingRole,
          );

          // If user has a role in this crew, only show users from this crew
          if (userHasRoleInCrew) {
            filteredUsers = filteredUsers.filter((item) => item.Crew?.id === crew.id);
          }
        }

        // Additionally filter by training_role if specified
        if (trainingRole) {
          filteredUsers = filteredUsers.filter((item) => item.training_role === "user");
        }

        // Set state values from response
        setELearning(res.is_e_learning ?? 0);
        setClassroom(res.is_classroom ?? 0);
        setPractical(res.is_practical ?? 0);
        setClassroomAttendance(res.classroom_attendance ?? 0);
        setClassroomCheck(res.classroom_check ?? 0);
        setInstructorComplete(res.instructor_complete ?? 0);
        setAssessorPassed(res.assessor_passed ?? 0);
        setCheckerPassed(res.checker_passed ?? 0);

        // Set course info
        if (res.Course) {
          setCourseId(res.Course.id);
        }

        setUsersInfo(filteredUsers);
      } catch (error) {
        console.error("Error fetching training info:", error);
        setUsersInfo([]);
      }
    })();
  }, [refresh, crew, match.params]);

  const onEventSwitch = async (id, field, value, userInfo) => {
    const res = await updateCheckingProgress(id, field, value, evaluator.id);

    let resAfterUpdateModule = null;
    if (classroom) {
      const classroom_status = isClassroomModuleCompleted({ classroomAttendance, classroomCheck }, res) ? 1 : 0;
      resAfterUpdateModule = await updateModuleStatus(id, "classroom_status", classroom_status);
    }

    if (practical) {
      const practical_status = isPracticalModuleCompleted({ instructorComplete, assessorPassed, checkerPassed }, res)
        ? 1
        : 0;
      resAfterUpdateModule = await updateModuleStatus(id, "practical_status", practical_status);
    }

    if (userInfo.User?.id) {
      await updateStatusCertificate(
        userInfo.User.id,
        courseId,
        isTrainingCompleted(
          { is_e_learning: eLearning, is_classroom: classroom, is_practical: practical },
          resAfterUpdateModule,
        )
          ? 1
          : 0,
      );
    }
    setRefresh(refresh * -1);
  };

  const refreshRows = () => setRefresh((current) => current * -1);

  return (
    <>
      <Typography variant="h4">{t("training_pages.users_training")}</Typography>
      {usersInfo && (
        <TableComponent
          search={["displayname", "fullName", "companyName"]}
          pagination
          items={usersInfo}
          header={[
            { title: t("training_pages.training_user_id"), style: { width: "5%" } },
            { title: t("user") },
            { title: t("training_pages.crew") },
            { title: t("training_pages.e_learning") },
            { title: t("training_pages.classroom") },
            { title: t("training_pages.practical") },
            { title: t("actions") },
          ]}
          renderChildren={(userInfo) => (
            <TableRow key={userInfo.id}>
              <TableCell>{userInfo.id}</TableCell>
              <TableCell>{userInfo.User?.fullname || t("not_available")}</TableCell>
              <TableCell>{userInfo.Crew?.companyName || t("not_available")}</TableCell>

              <TableCell>{eLearning == 1 ? userInfo.elearning_status ? <OkIcon /> : <ExpiredIcon /> : ""}</TableCell>

              <TableCell>
                {classroom == 1 ? (
                  <>
                    {userInfo.classroom_status ? <OkIcon /> : <ExpiredIcon />}
                    {classroomAttendance == 1 ? (
                      <SignOffControl
                        label={t("training_pages.attendance")}
                        title={t("training.classroom_attendance")}
                        field="classroom_attendance"
                        userInfo={userInfo}
                        showSwitch={!userInfo.classroom_status}
                        onToggle={onEventSwitch}
                        onSaved={refreshRows}
                      />
                    ) : (
                      ""
                    )}
                    {classroomCheck == 1 ? (
                      <SignOffControl
                        label={t("training_pages.check")}
                        title={t("training.classroom_test")}
                        field="classroom_test"
                        userInfo={userInfo}
                        showSwitch={!userInfo.classroom_status}
                        onToggle={onEventSwitch}
                        onSaved={refreshRows}
                      />
                    ) : (
                      ""
                    )}
                  </>
                ) : (
                  ""
                )}
              </TableCell>

              <TableCell>
                {practical == 1 ? (
                  <>
                    {userInfo.practical_status ? <OkIcon /> : <ExpiredIcon />}
                    {instructorComplete == 1 ? (
                      <SignOffControl
                        label={t("training_pages.instructor")}
                        title={t("training_pages.instructor_complete")}
                        field="instructor_complete"
                        userInfo={userInfo}
                        showSwitch={!userInfo.practical_status}
                        onToggle={onEventSwitch}
                        onSaved={refreshRows}
                      />
                    ) : (
                      ""
                    )}
                    {assessorPassed == 1 ? (
                      <SignOffControl
                        label={t("training_pages.assessor")}
                        title={t("training_pages.assessor_passed")}
                        field="assessor_passed"
                        userInfo={userInfo}
                        showSwitch={!userInfo.practical_status}
                        onToggle={onEventSwitch}
                        onSaved={refreshRows}
                      />
                    ) : (
                      ""
                    )}
                    {checkerPassed == 1 ? (
                      <SignOffControl
                        label={t("training_pages.checker")}
                        title={t("training_pages.checker_passed")}
                        field="checker_passed"
                        userInfo={userInfo}
                        showSwitch={!userInfo.practical_status}
                        onToggle={onEventSwitch}
                        onSaved={refreshRows}
                      />
                    ) : (
                      ""
                    )}
                  </>
                ) : (
                  ""
                )}
              </TableCell>

              <TableCell>
                {eLearning == userInfo.elearning_status &&
                classroom == userInfo.classroom_status &&
                practical == userInfo.practical_status ? (
                  <Button
                    title={t("training_pages.download_certificate")}
                    icon={<GetAppIcon />}
                    onClick={() => openExportWindow(courseId, userInfo.user_id, onExportError)}
                  />
                ) : (
                  ""
                )}
              </TableCell>
            </TableRow>
          )}
        />
      )}

      <Button title={t("navigate_back")} type="button" onClick={() => history.goBack()}>
        {t("training_pages.return_to_all_trainings")}
      </Button>
    </>
  );
};

export default UsersTrainingInscribedTable;
